/**
 * barristersStore — API-backed barrister roster.
 *
 * Replaces the hardcoded seed array. All barrister data now comes from
 * GET /barristers (chambersId-scoped, real DB). The exported hook and
 * helper functions preserve their existing signatures so consumer pages
 * (clerk DashboardPage, MasterDiaryPage) require no changes.
 */
import { useMemo, useEffect } from 'react';
import { useApiBarristersStore, type ApiBarrister } from './barristersApi';

// ─── Shared types (consumers depend on these) ─────────────────

export type BarristerRole = 'silk' | 'junior' | 'pupil' | 'door_tenant';
export type Availability = 'Available' | 'In court' | 'On leave' | 'Busy';

export interface PracticeArea {
  area: string;
  level: 'Lead' | 'Senior' | 'Junior';
}

export interface Qualification {
  title: string;
  year: number;
  sub: string;
}

export interface Barrister {
  id: string;
  name: string;
  shortName?: string;
  initials: string;
  role: BarristerRole;
  call: number;
  silk?: number;
  email: string;
  phone: string;
  location?: string;
  url?: string;
  available: Availability;
  bio?: string;
  practiceAreas: PracticeArea[];
  qualifications?: Qualification[];
  languages?: string[];
  winRate?: number;
  feesMtd?: number;
  feesYtd?: number;
}

// ─── Mapping: ApiBarrister → Barrister ───────────────────────

function availabilityLabel(a: ApiBarrister['availability']): Availability {
  switch (a) {
    case 'available': return 'Available';
    case 'partial':   return 'Available';
    case 'busy':      return 'Busy';
    case 'on_leave':  return 'On leave';
    case 'unavailable': return 'Busy';
    default:          return 'Available';
  }
}

function expertiseLabel(level: string): 'Lead' | 'Senior' | 'Junior' {
  if (level === 'lead')   return 'Lead';
  if (level === 'senior') return 'Senior';
  return 'Junior';
}

function apiToBarrister(b: ApiBarrister): Barrister {
  const suffix = b.user.memberType === 'silk' ? ' KC' : '';
  const fullName = `${b.user.firstName} ${b.user.lastName}${suffix}`;
  const short = b.shortName || b.user.lastName + suffix;

  return {
    id: b.id,
    name: fullName,
    shortName: short,
    initials: b.initials || `${b.user.firstName[0] ?? ''}${b.user.lastName[0] ?? ''}`.toUpperCase(),
    role: b.user.memberType as BarristerRole,
    call: b.callYear ?? new Date().getFullYear() - b.seniority,
    silk: b.silkYear ?? undefined,
    email: b.user.email,
    phone: b.user.phone ?? '',
    location: b.inn ?? undefined,
    url: b.publicSlug ? `chambers/${b.publicSlug}` : undefined,
    available: availabilityLabel(b.availability),
    bio: undefined,
    practiceAreas: b.practiceAreas.map((pa) => ({
      area: pa.name,
      level: expertiseLabel(pa.expertiseLevel),
    })),
    qualifications: b.callYear
      ? [{ title: 'Called to the Bar', year: b.callYear, sub: b.inn ?? 'Inns of Court' }]
      : undefined,
    languages: ['English'],
    winRate: undefined,
    feesMtd: undefined,
    feesYtd: undefined,
  };
}

// ─── Drop-in replacement for the old Zustand store ───────────

interface BarristersStoreShape {
  barristers: Barrister[];
  getById: (id: string) => Barrister | undefined;
  getByName: (name: string) => Barrister | undefined;
}

/**
 * Drop-in replacement for the old hardcoded useBarristersStore.
 * Reads from the real API cache (useApiBarristersStore) and maps
 * ApiBarrister → Barrister on every call. Consumers see no change.
 */
export function useBarristersStore<T>(selector: (state: BarristersStoreShape) => T): T {
  const apiBarristers = useApiBarristersStore((s) => s.barristers);
  const loaded = useApiBarristersStore((s) => s.loaded);
  const fetch  = useApiBarristersStore((s) => s.fetch);

  // Auto-fetch on first use — no consumer needs to call fetch() explicitly.
  useEffect(() => {
    if (!loaded) fetch();
  }, [loaded, fetch]);

  const barristers = useMemo(
    () => apiBarristers.map(apiToBarrister),
    [apiBarristers],
  );

  const state: BarristersStoreShape = useMemo(() => ({
    barristers,
    getById:   (id)   => barristers.find((b) => b.id === id),
    getByName: (name) => barristers.find((b) => b.name === name || b.shortName === name),
  }), [barristers]);

  return selector(state);
}

// ─── Constants ────────────────────────────────────────────────

export const ROLE_LABEL: Record<BarristerRole, string> = {
  silk:        'KC / Silk',
  junior:      'Junior',
  pupil:       'Pupil',
  door_tenant: 'Door tenant',
};

// ─── Name-matching utility ────────────────────────────────────

const POST_NOMS = new Set(['KC', 'QC', 'SC', 'MP', 'CBE', 'DBE', 'OBE']);

function stripPostNoms(s: string): string {
  return s.split(/\s+/).filter((p) => !POST_NOMS.has(p)).join(' ').trim();
}

/**
 * Returns true if `candidate` (a loose string from an event.barrister or
 * matter.barrister field) refers to barrister `b`. Handles:
 *   "James Thornton KC", "Thornton KC", "Thornton", with/without post-nominals.
 */
export function barristerNameMatches(
  candidate: string | undefined | null,
  b: Barrister,
): boolean {
  if (!candidate) return false;
  if (candidate === b.name || candidate === b.shortName) return true;
  const c = stripPostNoms(candidate);
  const n = stripPostNoms(b.name);
  const s = b.shortName ? stripPostNoms(b.shortName) : '';
  if (c === n || (s && c === s)) return true;
  const cLast = c.split(' ').slice(-1)[0] ?? '';
  const nLast = n.split(' ').slice(-1)[0] ?? '';
  return !!cLast && !!nLast && cLast === nLast;
}
